import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import { startImpersonation } from '../utils/impersonation';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

const ROLE_LABELS = {
  superadmin: '👑 Суперадмин',
  admin: '🔧 Админ',
  manager: '👔 Менеджер',
  client: '👤 Клиент',
};

const ROLE_COLORS = {
  superadmin: '#f59e0b',
  admin: '#8b5cf6',
  manager: '#10b981',
  client: '#6b7280',
};

export default function AdminUsers() {
  const { user } = useApp();
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editMode, setEditMode] = useState(null); // 'role' | 'commission' | 'delete'
  const [deleteMode, setDeleteMode] = useState('soft');
  const [confirmText, setConfirmText] = useState('');
  const [confirmCheck, setConfirmCheck] = useState(false);
  const [deleteDocsCheck, setDeleteDocsCheck] = useState(false);
  const [reason, setReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [commissionValue, setCommissionValue] = useState(0);

  const [filterPaid, setFilterPaid] = useState('all'); // all | paid | unpaid
  const [filterVerified, setFilterVerified] = useState('all'); // all | verified | not_verified
  const [filterBlocked, setFilterBlocked] = useState('all'); // all | blocked | active

  const isSuperadmin = user?.role === 'superadmin';

  useEffect(() => {
    loadUsers();
  }, [includeDeleted]);

  async function loadUsers() {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users?include_deleted=${includeDeleted}&limit=500`);
      setUsers(data.users || []);
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка загрузки' });
    } finally {
      setLoading(false);
    }
  }

  function openUserModal(u) {
    setSelectedUser(u);
    setEditMode(null);
    setCommissionValue(u.manager_commission_rate || 0);
  }

  function closeModal() {
    setSelectedUser(null);
    setEditMode(null);
    setConfirmText('');
    setConfirmCheck(false);
    setDeleteDocsCheck(false);
    setReason('');
  }

  async function setRole(role) {
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${selectedUser.id}/set-role`, { role });
      setMessage({ type: 'success', text: `Роль изменена на ${role}` });
      loadUsers();
      closeModal();
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка' });
    } finally {
      setActionLoading(false);
    }
  }

  async function setCommission() {
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${selectedUser.id}/set-commission`, { commission_rate: commissionValue });
      setMessage({ type: 'success', text: `Комиссия установлена: ${commissionValue}%` });
      loadUsers();
      closeModal();
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка' });
    } finally {
      setActionLoading(false);
    }
  }

  async function grantAccess(grant) {
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${selectedUser.id}/grant-access`, { grant });
      setMessage({ type: 'success', text: grant ? '✅ Доступ открыт!' : '❌ Доступ закрыт' });
      loadUsers();
      closeModal();
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка' });
    } finally {
      setActionLoading(false);
    }
  }

  async function assignManager(managerId) {
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${selectedUser.id}/assign-manager`, { manager_id: managerId || null });
      setMessage({ type: 'success', text: managerId ? 'Менеджер назначен' : 'Менеджер удалён' });
      loadUsers();
      closeModal();
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка' });
    } finally {
      setActionLoading(false);
    }
  }

  async function setProfileStatus(status) {
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${selectedUser.id}/set-profile-status`, { status });
      setMessage({ type: 'success', text: `Профиль: ${status}` });
      loadUsers();
      closeModal();
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка' });
    } finally {
      setActionLoading(false);
    }
  }

  async function setBlocked(blocked) {
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${selectedUser.id}/set-blocked`, { blocked });
      setMessage({ type: 'success', text: blocked ? 'Пользователь заблокирован' : 'Пользователь разблокирован' });
      loadUsers();
      closeModal();
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка' });
    } finally {
      setActionLoading(false);
    }
  }

  async function handleSoftDelete() {
    if (!confirmCheck) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${selectedUser.id}/soft-delete`, { reason });
      setMessage({ type: 'success', text: `Пользователь архивирован` });
      loadUsers();
      closeModal();
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка' });
    } finally {
      setActionLoading(false);
    }
  }

  async function handleHardDelete() {
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${selectedUser.id}/hard-delete`, {
        confirmation_text: `DELETE ${selectedUser.telegram_id}`,
        reason: 'Admin action',
        delete_documents: true,
      });
      setMessage({ type: 'success', text: `Пользователь удалён` });
      loadUsers();
      closeModal();
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка' });
    } finally {
      setActionLoading(false);
    }
  }

  async function handleRestore(u) {
    try {
      await api.post(`/admin/users/${u.id}/restore`, {});
      setMessage({ type: 'success', text: `Пользователь восстановлен` });
      loadUsers();
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка' });
    }
  }

  const managers = users.filter(u => ['superadmin', 'admin', 'manager'].includes(u.role) && !u.is_deleted);

  const filteredUsers = users.filter(u => {
    if (filterPaid === 'paid' && !u.has_paid_access) return false;
    if (filterPaid === 'unpaid' && u.has_paid_access) return false;
    if (filterVerified === 'verified' && u.profile_status !== 'verified') return false;
    if (filterVerified === 'not_verified' && u.profile_status === 'verified') return false;
    if (filterBlocked === 'blocked' && !u.is_blocked) return false;
    if (filterBlocked === 'active' && u.is_blocked) return false;
    return true;
  });

  return (
    <div className="page">
      <BackButton />
      <h1>👥 Пользователи</h1>

      {message && (
        <div style={{ 
          padding: 12, 
          borderRadius: 8, 
          marginBottom: 16,
          background: message.type === 'error' ? 'rgba(220,38,38,0.2)' : 'rgba(16,185,129,0.2)',
        }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#fff' }}>✕</button>
        </div>
      )}

      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input 
            type="checkbox" 
            checked={includeDeleted} 
            onChange={(e) => setIncludeDeleted(e.target.checked)}
          />
          Показать удалённых
        </label>

        <select value={filterPaid} onChange={(e) => setFilterPaid(e.target.value)}>
          <option value="all">Оплата: все</option>
          <option value="paid">Оплата: есть</option>
          <option value="unpaid">Оплата: нет</option>
        </select>

        <select value={filterVerified} onChange={(e) => setFilterVerified(e.target.value)}>
          <option value="all">Верификация: все</option>
          <option value="verified">Верифицирован</option>
          <option value="not_verified">Не верифицирован</option>
        </select>

        <select value={filterBlocked} onChange={(e) => setFilterBlocked(e.target.value)}>
          <option value="all">Блок: все</option>
          <option value="active">Активные</option>
          <option value="blocked">Заблокированные</option>
        </select>

        <button className="btn btn-secondary" onClick={loadUsers} disabled={loading}>
          🔄 Обновить
        </button>
      </div>

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : (
        <div className="list">
          {filteredUsers.map((u) => {
            const managerName = u.manager_id 
              ? users.find(m => m.id === u.manager_id)?.first_name || `ID:${u.manager_id}`
              : null;
            
            return (
              <div 
                key={u.id} 
                className="list-item"
                style={{ 
                  cursor: 'pointer',
                  opacity: u.is_deleted ? 0.5 : 1,
                  background: u.is_deleted ? 'rgba(255,0,0,0.05)' : undefined,
                }}
                onClick={() => !u.is_deleted && openUserModal(u)}
              >
                <div className="list-main">
                  <div className="list-title" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ 
                      background: ROLE_COLORS[u.role], 
                      padding: '2px 8px', 
                      borderRadius: 4, 
                      fontSize: 11 
                    }}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                    {u.first_name} {u.last_name || ''}
                    {u.manager_commission_rate > 0 && (
                      <span style={{ fontSize: 11, color: '#10b981' }}>💰{u.manager_commission_rate}%</span>
                    )}
                    <span style={{ fontSize: 11, color: u.has_paid_access ? '#10b981' : '#f59e0b' }}>
                      {u.has_paid_access ? '💳 paid' : '💳 no'}
                    </span>
                    <span style={{ fontSize: 11, color: u.profile_status === 'verified' ? '#10b981' : '#f59e0b' }}>
                      {u.profile_status === 'verified' ? '✅ verified' : '⏳ not verified'}
                    </span>
                    {u.is_blocked && (
                      <span style={{ fontSize: 11, color: '#ef4444' }}>⛔ blocked</span>
                    )}
                  </div>
                  <div className="list-sub">
                    @{u.telegram_username || u.telegram_id}
                    {managerName && <span style={{ marginLeft: 8, color: '#10b981' }}>← {managerName}</span>}
                  </div>
                </div>
                {u.is_deleted ? (
                  <button 
                    className="btn btn-small"
                    onClick={(e) => { e.stopPropagation(); handleRestore(u); }}
                  >
                    ↩️
                  </button>
                ) : (
                  <span style={{ opacity: 0.5 }}>→</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* User Edit Modal */}
      {selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 16,
        }}>
          <div style={{
            background: '#1a1a2e',
            borderRadius: 12,
            padding: 24,
            maxWidth: 450,
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
          }}>
            {/* Header */}
            <h2 style={{ marginTop: 0 }}>
              {selectedUser.first_name} {selectedUser.last_name || ''}
            </h2>
            <p style={{ opacity: 0.7, marginBottom: 8 }}>
              @{selectedUser.telegram_username || selectedUser.telegram_id}
            </p>
            <div style={{ 
              display: 'inline-block',
              background: ROLE_COLORS[selectedUser.role], 
              padding: '6px 12px', 
              borderRadius: 6,
              marginBottom: 16 
            }}>
              {ROLE_LABELS[selectedUser.role]}
              {selectedUser.manager_commission_rate > 0 && ` • ${selectedUser.manager_commission_rate}%`}
            </div>

            {/* Main Menu */}
            {!editMode && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {isSuperadmin && selectedUser.role !== 'superadmin' && (
                  <button 
                    className="btn" 
                    style={{ background: '#7c3aed' }}
                    onClick={async () => {
                      setActionLoading(true);
                      try {
                        const { data } = await api.post(`/admin/impersonate/${selectedUser.id}`);
                        startImpersonation({
                          token: data.access_token || data.token,
                          mode: 'user',
                          adminUser: user,
                        });
                        window.location.reload();
                      } catch (e) {
                        setMessage({ type: 'error', text: e.userMessage || 'Ошибка impersonation' });
                      } finally {
                        setActionLoading(false);
                      }
                    }}
                    disabled={actionLoading}
                  >
                    👤 Войти как пользователь
                  </button>
                )}
                <button className="btn btn-primary" onClick={() => setEditMode('role')} disabled={actionLoading}>
                  🎭 Изменить роль
                </button>
                <button className="btn btn-secondary" onClick={() => setEditMode('commission')} disabled={actionLoading}>
                  💰 Установить комиссию
                </button>
                <button className="btn btn-secondary" onClick={() => setEditMode('manager')} disabled={actionLoading}>
                  👔 Назначить менеджера
                </button>
                <button 
                  className="btn" 
                  style={{ background: selectedUser.has_paid_access ? '#10b981' : '#6366f1' }}
                  onClick={() => grantAccess(!selectedUser.has_paid_access)} 
                  disabled={actionLoading}
                >
                  {selectedUser.has_paid_access ? '✅ Доступ открыт (закрыть)' : '🔓 Открыть доступ'}
                </button>

                {isSuperadmin && (
                  <button
                    className="btn"
                    style={{ background: selectedUser.profile_status === 'verified' ? '#f59e0b' : '#10b981' }}
                    onClick={() => setProfileStatus(selectedUser.profile_status === 'verified' ? 'pending' : 'verified')}
                    disabled={actionLoading}
                  >
                    {selectedUser.profile_status === 'verified' ? '⛔ Снять верификацию' : '✅ Верифицировать'}
                  </button>
                )}

                {isSuperadmin && (
                  <button
                    className="btn"
                    style={{ background: selectedUser.is_blocked ? '#10b981' : '#ef4444' }}
                    onClick={() => setBlocked(!selectedUser.is_blocked)}
                    disabled={actionLoading}
                  >
                    {selectedUser.is_blocked ? '✅ Разблокировать' : '⛔ Заблокировать'}
                  </button>
                )}

                {selectedUser.role !== 'superadmin' && (
                  <button 
                    className="btn" 
                    style={{ background: '#dc2626' }}
                    onClick={() => { setEditMode('delete'); setDeleteMode('soft'); setConfirmCheck(false); }}
                    disabled={actionLoading}
                  >
                    🗑 Удалить
                  </button>
                )}
                <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={closeModal}>
                  Закрыть
                </button>
              </div>
            )}

            {/* Role Selection */}
            {editMode === 'role' && (
              <div>
                <h4>Выберите роль:</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {['client', 'manager', 'admin', 'superadmin'].map(role => (
                    <button
                      key={role}
                      className={`btn ${selectedUser.role === role ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ background: selectedUser.role === role ? ROLE_COLORS[role] : undefined }}
                      onClick={() => setRole(role)}
                      disabled={actionLoading}
                    >
                      {ROLE_LABELS[role]}
                    </button>
                  ))}
                  <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={() => setEditMode(null)}>
                    ← Назад
                  </button>
                </div>
              </div>
            )}

            {/* Commission */}
            {editMode === 'commission' && (
              <div>
                <h4>💰 Комиссия менеджера</h4>
                <p style={{ fontSize: 13, opacity: 0.7 }}>% от прибыли подчинённых</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={commissionValue}
                    onChange={(e) => setCommissionValue(Number(e.target.value))}
                    style={{ flex: 1 }}
                  />
                  <span style={{ minWidth: 60, fontWeight: 'bold', fontSize: 18 }}>{commissionValue}%</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary" onClick={() => setEditMode(null)}>← Назад</button>
                  <button className="btn btn-primary" onClick={setCommission} disabled={actionLoading}>
                    {actionLoading ? '⏳...' : '💾 Сохранить'}
                  </button>
                </div>
              </div>
            )}

            {/* Manager Assignment */}
            {editMode === 'manager' && (
              <div>
                <h4>👔 Назначить менеджера</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflow: 'auto' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => assignManager(null)}
                    disabled={actionLoading}
                  >
                    ❌ Без менеджера
                  </button>
                  {managers
                    .filter(m => m.id !== selectedUser.id)
                    .map(m => (
                      <button
                        key={m.id}
                        className={`btn ${selectedUser.manager_id === m.id ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => assignManager(m.id)}
                        disabled={actionLoading}
                      >
                        {ROLE_LABELS[m.role]} {m.first_name} {m.last_name || ''}
                      </button>
                    ))}
                </div>
                <button className="btn btn-secondary" style={{ marginTop: 12, width: '100%' }} onClick={() => setEditMode(null)}>
                  ← Назад
                </button>
              </div>
            )}

            {/* Delete */}
            {editMode === 'delete' && (
              <div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  <button
                    className={`btn ${deleteMode === 'soft' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => { setDeleteMode('soft'); setConfirmCheck(false); }}
                  >
                    📦 Архив
                  </button>
                  {isSuperadmin && (
                    <button
                      className={`btn ${deleteMode === 'hard' ? '' : 'btn-secondary'}`}
                      style={{ background: deleteMode === 'hard' ? '#dc2626' : undefined }}
                      onClick={() => { setDeleteMode('hard'); setConfirmCheck(false); setConfirmText(''); }}
                    >
                      🗑 Полное удаление
                    </button>
                  )}
                </div>

                {deleteMode === 'hard' ? (
                  <div style={{ background: 'rgba(220,38,38,0.2)', padding: 12, borderRadius: 8, marginBottom: 12 }}>
                    ⚠️ Полное удаление необратимо!
                  </div>
                ) : (
                  <label style={{ display: 'flex', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
                    <input type="checkbox" checked={confirmCheck} onChange={(e) => setConfirmCheck(e.target.checked)} />
                    Подтверждаю архивирование
                  </label>
                )}

                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary" onClick={() => setEditMode(null)}>← Назад</button>
                  <button
                    className="btn"
                    style={{ background: '#dc2626' }}
                    disabled={actionLoading || (deleteMode !== 'hard' && !confirmCheck)}
                    onClick={deleteMode === 'hard' ? handleHardDelete : handleSoftDelete}
                  >
                    {actionLoading ? '⏳...' : deleteMode === 'hard' ? '🗑 Удалить полностью' : '📦 Архивировать'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
